import React, { useState, useEffect } from 'react';
import apiService from '../services/ApiService';
import { CustomDatePicker } from './CustomDatePicker';
import codeValidationService from '../services/CodeValidationService';
import { useAuth } from '../contexts/AuthContext';
import CustomDropdown from './CustomDropdown';
import Modal from './ui/Modal';
import Button from './ui/Button';

interface RegisterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRegister: () => void;
  onSwitchToLogin: () => void;
}

interface Country {
  code: string;
  name: string;
  continent: string;
  sort_order: number;
}

interface StateProvince {
  code: string;
  name: string;
  type: string;
}

export const RegisterModal: React.FC<RegisterModalProps> = ({
  isOpen,
  onClose,
  onRegister,
  onSwitchToLogin
}) => {
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    registrationCode: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    country: '',
    stateProvince: '',
    gender: '',
    birthDate: '',
    instagramUsername: '',
    facebookUrl: '',
    websiteUrl: '',
    bio: ''
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [countries, setCountries] = useState<Country[]>([]);
  const [statesProvinces, setStatesProvinces] = useState<StateProvince[]>([]);
  const [loadingStates, setLoadingStates] = useState(false);

  // Registration code validation state
  const [codeValidation, setCodeValidation] = useState<{
    isValidating: boolean;
    isValid: boolean | null;
    message: string;
    codeDetails?: any;
  }>({
    isValidating: false,
    isValid: null,
    message: ''
  });

  const RESERVED_USERNAMES = [
    'login',
    'register',
    'admin',
    'v',
    'garage',
    'events',
    'organizer',
    'judge',
    'discover',
    'clubs',
    'badges',
    'invite',
    'favorites',
    'shops',
    'search',
    'settings',
  ];
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [checkingUsername, setCheckingUsername] = useState(false);

  // Load countries on component mount
  useEffect(() => {
    if (isOpen) {
      loadCountries();
      
      // Check for registration code in URL
      const codeFromUrl = codeValidationService.parseCodeFromUrl();
      if (codeFromUrl) {
        setFormData(prev => ({ ...prev, registrationCode: codeFromUrl }));
        validateRegistrationCode(codeFromUrl);
      }
    }
  }, [isOpen]);

  // Load states/provinces when country changes
  useEffect(() => {
    if (formData.country) {
      loadStatesProvinces(formData.country);
    } else {
      setStatesProvinces([]);
      setFormData(prev => ({ ...prev, stateProvince: '' }));
    }
  }, [formData.country]);

  // Validate registration code when it changes
  useEffect(() => {
    if (formData.registrationCode && formData.registrationCode.length > 5) {
      const timeoutId = setTimeout(() => {
        validateRegistrationCode(formData.registrationCode);
      }, 500); // Debounce validation
      
      return () => clearTimeout(timeoutId);
    } else if (formData.registrationCode === '') {
      setCodeValidation({
        isValidating: false,
        isValid: null,
        message: ''
      });
    }
  }, [formData.registrationCode]);

  const loadCountries = async () => {
    try {
      const countriesData = await apiService.getCountries();
      setCountries(countriesData);
    } catch (error) {
      console.error('Failed to load countries:', error);
    }
  };

  const loadStatesProvinces = async (countryCode: string) => {
    setLoadingStates(true);
    try {
      const statesData = await apiService.getStatesProvinces(countryCode);
      setStatesProvinces(statesData);
    } catch (error) {
      console.error('Failed to load states/provinces:', error);
      setStatesProvinces([]);
    } finally {
      setLoadingStates(false);
    }
  };

  const validateRegistrationCode = async (code: string) => {
    if (!code.trim()) {
      setCodeValidation({
        isValidating: false,
        isValid: null,
        message: ''
      });
      return;
    }

    setCodeValidation({
      isValidating: true,
      isValid: null,
      message: 'Validating code...'
    });

    try {
      const result = await codeValidationService.validateCode(code);
      
      setCodeValidation({
        isValidating: false,
        isValid: result.isValid,
        message: result.isValid 
          ? `Valid invitation code${result.description ? ` - ${result.description}` : ''}` 
          : result.error || 'Invalid registration code',
        codeDetails: result.isValid ? result : undefined
      });
    } catch (error) {
      setCodeValidation({
        isValidating: false,
        isValid: false,
        message: 'Failed to validate code. Please try again.'
      });
    }
  };

  const checkUsernameAvailability = async (username: string) => {
    if (!username || username.length < 3) {
      setUsernameAvailable(null);
      return;
    }
    setCheckingUsername(true);
    try {
      const available = await apiService.checkUsernameAvailability(username);
      setUsernameAvailable(available);
      if (!available) {
        setErrors(prev => ({ ...prev, username: 'Username is already taken' }));
      }
    } catch (err) {
      setUsernameAvailable(null);
    } finally {
      setCheckingUsername(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    // Registration code validation (now required)
    if (!formData.registrationCode.trim()) {
      newErrors.registrationCode = 'Registration code is required';
    } else if (!codeValidation.isValid) {
      newErrors.registrationCode = codeValidation.message || 'Invalid registration code';
    }

    // Username validation
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain letters, numbers, and underscores';
    } else if (RESERVED_USERNAMES.includes(formData.username.toLowerCase())) {
      newErrors.username = 'This username is reserved. Please choose another.';
    } else if (usernameAvailable === false) {
      newErrors.username = 'Username is already taken';
    } else if (checkingUsername || usernameAvailable === null) {
      newErrors.username = checkingUsername
        ? 'Please wait while we check username availability'
        : 'Please verify that your username is available';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    // Country validation (now required)
    if (!formData.country) {
      newErrors.country = 'Country is required';
    }

    // State/Province validation (now required)
    if (!formData.stateProvince) {
      newErrors.stateProvince = 'State/Province is required';
    }

    // Gender validation (now required)
    if (!formData.gender) {
      newErrors.gender = 'Gender is required';
    }

    // Birth date validation (now required)
    if (!formData.birthDate) {
      newErrors.birthDate = 'Birth date is required';
    } else {
      const today = new Date();
      const birthDate = new Date(formData.birthDate);
      const age = today.getFullYear() - birthDate.getFullYear();
      
      if (birthDate > today) {
        newErrors.birthDate = 'Birth date cannot be in the future';
      } else if (age > 120) {
        newErrors.birthDate = 'Please enter a valid birth date';
      } else if (age < 13) {
        newErrors.birthDate = 'You must be at least 13 years old to register';
      }
    }

    // Instagram username validation
    if (formData.instagramUsername && !/^[a-zA-Z0-9_.]+$/.test(formData.instagramUsername)) {
      newErrors.instagramUsername = 'Instagram username can only contain letters, numbers, dots, and underscores';
    }

    // URL validations
    if (formData.facebookUrl && !isValidUrl(formData.facebookUrl)) {
      newErrors.facebookUrl = 'Please enter a valid Facebook URL';
    }

    if (formData.websiteUrl && !isValidUrl(formData.websiteUrl)) {
      newErrors.websiteUrl = 'Please enter a valid website URL';
    }

    // Bio validation
    if (formData.bio && formData.bio.length > 500) {
      newErrors.bio = 'Bio must be less than 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setGeneralError('');

    try {
      await apiService.register({
        registrationCode: formData.registrationCode.trim(),
        username: formData.username.trim(),
        email: formData.email.trim(),
        password: formData.password,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        country: formData.country,
        stateProvince: formData.stateProvince,
        gender: formData.gender,
        birthDate: formData.birthDate,
        instagramUsername: formData.instagramUsername || undefined,
        facebookUrl: formData.facebookUrl || undefined,
        websiteUrl: formData.websiteUrl || undefined,
        bio: formData.bio || undefined,
      });

      // Registration successful, now login
      await login(formData.username.trim(), formData.password);
      onRegister();
      onClose();

    } catch (error: any) {
      console.error('Registration failed:', error);
      setGeneralError(error.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'non_binary', label: 'Non-binary' },
    { value: 'other', label: 'Other' },
    { value: 'prefer_not_to_say', label: 'Prefer not to say' }
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Start your garage"
      description="Add a build, document the story, and share a public profile."
      size="lg"
      className="max-h-[90vh] overflow-y-auto"
    >
      {generalError && (
        <div role="alert" className="mb-4 p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-200 text-sm">
          {generalError}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on" method="post">
            {/* Registration Code Section */}
            <div className="border-b border-white/[0.08] pb-6">
              <h3 className="text-lg font-medium text-white mb-4">Invitation Code</h3>
              
              <div className="space-y-2">
                <label htmlFor="registrationCode" className="input-label">
                  Invitation code *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="registrationCode"
                    name="registrationCode"
                    value={formData.registrationCode}
                    onChange={(e) => handleInputChange('registrationCode', e.target.value.toUpperCase())}
                    className={`input pr-10 ${
                      errors.registrationCode
                        ? 'border-red-500'
                        : codeValidation.isValid === true
                          ? 'border-emerald-500'
                          : codeValidation.isValid === false
                            ? 'border-red-500'
                            : ''
                    }`}
                    placeholder="XXXXXXXX-XXXXXXXX-XXXXXXXX-XXXXXXXX"
                    autoComplete="off"
                    disabled={isLoading}
                    style={{ textTransform: 'uppercase' }}
                  />
                  
                  {/* Validation Icon */}
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    {codeValidation.isValidating ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500"></div>
                    ) : codeValidation.isValid === true ? (
                      <svg className="h-5 w-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : codeValidation.isValid === false ? (
                      <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    ) : null}
                  </div>
                </div>
                
                {/* Validation Message */}
                {codeValidation.message && (
                  <p className={`text-sm ${
                    codeValidation.isValid === true 
                      ? 'text-emerald-400' 
                      : codeValidation.isValid === false 
                        ? 'text-red-400' 
                        : 'text-muted'
                  }`}>
                    {codeValidation.message}
                  </p>
                )}
                
                {/* Error Message */}
                {errors.registrationCode && (
                  <p className="input-error">{errors.registrationCode}</p>
                )}
                
                <p className="input-helper">
                  Don&apos;t have a code? DM <a href="https://instagram.com/biltspec" target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:text-orange-300 font-medium">@biltspec</a> on Instagram.
                </p>
              </div>
            </div>

            {/* Required Fields Section */}
            <div className="border-b border-white/[0.08] pb-6">
              <h3 className="text-lg font-medium text-white mb-4">The essentials</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Username */}
                <div>
                  <label htmlFor="username" className="input-label">
                    Username *
                  </label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    onBlur={() => checkUsernameAvailability(formData.username)}
                    className={`input ${errors.username ? 'border-red-500' : ''}`}
                    placeholder="Choose a username"
                    autoComplete="username"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck="false"
                    disabled={isLoading}
                  />
                  {checkingUsername && (
                    <p className="text-xs text-muted mt-1">Checking username availability...</p>
                  )}
                  {usernameAvailable === false && !errors.username && (
                    <p className="text-xs text-red-400 mt-1">Username is already taken</p>
                  )}
                  {RESERVED_USERNAMES.includes(formData.username.toLowerCase()) && (
                    <p className="text-xs text-red-400 mt-1">This username is reserved. Please choose another.</p>
                  )}
                  {errors.username && (
                    <p className="mt-1 text-sm text-red-400">{errors.username}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="input-label">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`input ${errors.email ? 'border-red-500' : ''}`}
                    placeholder="your.email@example.com"
                    autoComplete="email"
                    disabled={isLoading}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-400">{errors.email}</p>
                  )}
                </div>

                {/* First Name */}
                <div>
                  <label htmlFor="firstName" className="input-label">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className={`input ${errors.firstName ? 'border-red-500' : ''}`}
                    placeholder="Your first name"
                    autoComplete="given-name"
                    autoCapitalize="words"
                    disabled={isLoading}
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-400">{errors.firstName}</p>
                  )}
                </div>

                {/* Last Name */}
                <div>
                  <label htmlFor="lastName" className="input-label">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className={`input ${errors.lastName ? 'border-red-500' : ''}`}
                    placeholder="Your last name"
                    autoComplete="family-name"
                    autoCapitalize="words"
                    disabled={isLoading}
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-400">{errors.lastName}</p>
                  )}
                </div>

                {/* Password */}
                <div>
                  <label htmlFor="password" className="input-label">
                    Password *
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className={`input ${errors.password ? 'border-red-500' : ''}`}
                    placeholder="Create a strong password"
                    autoComplete="new-password"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck="false"
                    disabled={isLoading}
                  />
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-400">{errors.password}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-400">
                    Must be at least 8 characters with uppercase, lowercase, and number
                  </p>
                </div>

                {/* Confirm Password */}
                <div>
                  <label htmlFor="confirmPassword" className="input-label">
                    Confirm Password *
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    className={`input ${errors.confirmPassword ? 'border-red-500' : ''}`}
                    placeholder="Confirm your password"
                    autoComplete="new-password"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck="false"
                    disabled={isLoading}
                  />
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-400">{errors.confirmPassword}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Optional Profile Information */}
            <div className="border-b border-white/[0.08] pb-6">
              <h3 className="text-lg font-medium text-white mb-4">Profile details</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Country */}
                <div>
                  <label htmlFor="country" className="input-label">
                    Country *
                  </label>
                  <CustomDropdown
                    value={formData.country}
                    onChange={(value) => handleInputChange('country', value)}
                    options={countries.map((country) => ({
                      value: country.code,
                      label: country.name
                    }))}
                    placeholder="Select a country"
                    disabled={isLoading}
                    error={!!errors.country}
                  />
                  {errors.country && (
                    <p className="mt-1 text-sm text-red-400">{errors.country}</p>
                  )}
                </div>

                {/* State/Province */}
                <div>
                  <label htmlFor="stateProvince" className="input-label">
                    State/Province *
                  </label>
                  <CustomDropdown
                    value={formData.stateProvince}
                    onChange={(value) => handleInputChange('stateProvince', value)}
                    options={[
                      { 
                        value: '', 
                        label: loadingStates ? 'Loading...' : formData.country ? 'Select state/province' : 'Select country first'
                      },
                      ...statesProvinces.map((state) => ({
                        value: state.name,
                        label: state.name
                      }))
                    ]}
                    placeholder={loadingStates ? 'Loading...' : formData.country ? 'Select state/province' : 'Select country first'}
                    disabled={isLoading || !formData.country || loadingStates}
                    error={!!errors.stateProvince}
                  />
                  {errors.stateProvince && (
                    <p className="mt-1 text-sm text-red-400">{errors.stateProvince}</p>
                  )}
                </div>

                {/* Gender */}
                <div>
                  <label htmlFor="gender" className="input-label">
                    Gender *
                  </label>
                  <CustomDropdown
                    value={formData.gender}
                    onChange={(value) => handleInputChange('gender', value)}
                    options={genderOptions}
                    placeholder="Select gender"
                    disabled={isLoading}
                    error={!!errors.gender}
                  />
                  {errors.gender && (
                    <p className="mt-1 text-sm text-red-400">{errors.gender}</p>
                  )}
                </div>

                {/* Birth Date */}
                <div>
                  <CustomDatePicker
                    label="Birth Date *"
                    value={formData.birthDate}
                    onChange={(date) => handleInputChange('birthDate', date)}
                    placeholder="Select your birth date..."
                    disabled={isLoading}
                    className={errors.birthDate ? 'border-red-500' : ''}
                  />
                  {errors.birthDate && (
                    <p className="mt-1 text-sm text-red-400">{errors.birthDate}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="pb-6">
              <h3 className="text-lg font-medium text-white mb-4">Optional</h3>
              
              {/* Bio */}
              <div className="mb-4">
                <label htmlFor="bio" className="input-label">
                  Bio
                </label>
                <textarea
                  id="bio"
                  value={formData.bio}
                  onChange={(e) => handleInputChange('bio', e.target.value)}
                  className={`input resize-none ${errors.bio ? 'border-red-500' : ''}`}
                  placeholder="A short note about you or the cars you build…"
                  disabled={isLoading}
                  rows={3}
                  maxLength={500}
                />
                <div className="flex justify-between mt-1">
                  {errors.bio && (
                    <p className="text-sm text-red-400">{errors.bio}</p>
                  )}
                  <p className="text-xs text-gray-400 ml-auto">
                    {formData.bio.length}/500 characters
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Instagram */}
                <div>
                  <label htmlFor="instagramUsername" className="input-label">
                    Instagram Username
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-400">@</span>
                    <input
                      type="text"
                      id="instagramUsername"
                      value={formData.instagramUsername}
                      onChange={(e) => handleInputChange('instagramUsername', e.target.value)}
                      className={`input pl-8 ${errors.instagramUsername ? 'border-red-500' : ''}`}
                      placeholder="your_username"
                      disabled={isLoading}
                    />
                  </div>
                  {errors.instagramUsername && (
                    <p className="mt-1 text-sm text-red-400">{errors.instagramUsername}</p>
                  )}
                </div>

                {/* Facebook */}
                <div>
                  <label htmlFor="facebookUrl" className="input-label">
                    Facebook URL
                  </label>
                  <input
                    type="url"
                    id="facebookUrl"
                    value={formData.facebookUrl}
                    onChange={(e) => handleInputChange('facebookUrl', e.target.value)}
                    className={`input ${errors.facebookUrl ? 'border-red-500' : ''}`}
                    placeholder="https://facebook.com/yourprofile"
                    disabled={isLoading}
                  />
                  {errors.facebookUrl && (
                    <p className="mt-1 text-sm text-red-400">{errors.facebookUrl}</p>
                  )}
                </div>

                {/* Website */}
                <div className="md:col-span-2">
                  <label htmlFor="websiteUrl" className="input-label">
                    Website URL
                  </label>
                  <input
                    type="url"
                    id="websiteUrl"
                    value={formData.websiteUrl}
                    onChange={(e) => handleInputChange('websiteUrl', e.target.value)}
                    className={`input ${errors.websiteUrl ? 'border-red-500' : ''}`}
                    placeholder="https://yourwebsite.com"
                    disabled={isLoading}
                  />
                  {errors.websiteUrl && (
                    <p className="mt-1 text-sm text-red-400">{errors.websiteUrl}</p>
                  )}
                </div>
              </div>
            </div>

            <Button type="submit" variant="primary" loading={isLoading} fullWidth className="min-h-[48px]">
              Start your garage
            </Button>
          </form>

      <div className="mt-6 text-center">
        <p className="text-gray-400">
          Already have a garage?{' '}
          <button
            type="button"
            onClick={onSwitchToLogin}
            className="text-orange-400 hover:text-orange-300 font-medium transition-colors"
          >
            Sign in
          </button>
        </p>
      </div>
    </Modal>
  );
}; 